// Single source of truth for all site content.

export const PROFILE = {
  firstName: "Ananya",
  lastName: "Joshi",
  title: "AI Engineer / Software Developer",
  tagline: "Building AI-powered products end to end | LLMs, RAG & full-stack | Boston, MA",
  photo: "/images/profile.jpg",
  resume: "/resume.pdf",
  email: "joshi.ana@northeastern.edu",
  greetings: ["thanks for stopping by 👋", "powered by coffee ☕", "probably mid-lap 🏊‍♀️", "ask me about RAG 🤖"],
};

export const SOCIALS = [
  { name: "LinkedIn", icon: "linkedin", url: "https://www.linkedin.com/in/ananya-joshi-1268532b2/" },
  { name: "GitHub", icon: "github", url: "https://github.com/ananya1305" },
  { name: "Email", icon: "mail", url: "mailto:joshi.ana@northeastern.edu" },
];

// Newest first.
export const EDUCATION = [
  {
    degree: "MS in Artificial Intelligence",
    institution: "Northeastern University",
    location: "Boston, MA",
    dates: "Sep 2025 - Dec 2027",
    score: "GPA 3.83",
    tag: "Graduate",
    logo: "/images/logos/neu_logo_trim.png",
  },
  {
    degree: "BTech in Computer Science (AI)",
    institution: "NMIMS Mumbai",
    location: "Mumbai, India",
    dates: "Jul 2021 - Jun 2025",
    score: "GPA 3.5",
    tag: "Undergraduate",
    logo: "/images/logos/nmims_logo_trim.png",
  },
];

export const STACK = {
  label: "My primary tech stack",
  headline: "Python, PyTorch, React",
  columns: [
    ["LangGraph", "PyTorch", "FastAPI", "RAG"],
    ["React", "Node.js", "Qdrant", "Docker"],
  ],
};

export const BENTO = {
  // Photo for the big Short Profile tile. Falls back to PROFILE.photo if the file is missing.
  photo: "/images/profile-bento.png",
  hero: "AI engineer who builds the model and the interface around it",
  greetingsTitle: "Studying AI at Northeastern, Boston",
  greetings: [
    "Hello", "नमस्ते", "नमस्कार", "Hola", "Bonjour", "こんにちは", "Ciao", "Hallo", "안녕", "Olá", "你好", "Salut",
  ],
  badgeTitle: "Published at an IEEE conference, 2025",
  scoopLabel: "The Inside Scoop",
  scoopTitle: "National-level swimmer. My best ideas surface mid-lap",
  askTitle: "Do you want to ask a question?",
};

// Tech names map to Devicon slugs (https://devicon.dev). Unknown names fall back to initials.
export const PROJECTS = [
  {
    title: "Reddit-Augmented RL Stock Trader",
    category: "Reinforcement Learning",
    description: "PPO & DQN agents that trade on r/WallStreetBets sentiment (Sharpe 1.69 vs 1.22 on TSLA)",
    image: "/images/reddit-rl-trader.jpg",
    link: "https://github.com/ananya1305/reddit-rl-trader",
    linkLabel: "GitHub",
    demo: "https://ananya1305.github.io/reddit-rl-trader/",
    demoLabel: "View dashboard",
    tech: ["python", "pytorch", "numpy", "pandas", "jupyter"],
  },
  {
    title: "Baymax: X-Ray Analysis",
    link: "https://github.com/ananya1305/Baymax-XrayAnalysis",
    linkLabel: "GitHub",
    category: "Medical Imaging",
    description: "Fracture localization and chest disease classification (92% accuracy, 0.61 Dice)",
    image: "/images/baymax.jpeg",
    tech: ["python", "pytorch", "fastapi", "react", "docker"],
  },
  {
    title: "Auto Assess: SQL Evaluation",
    link: "https://github.com/ananya1305/AutoAssess-Automated-Evaluation-Platform-for-Enhanced-SQL-learning",
    linkLabel: "GitHub",
    category: "LLMs · IEEE 2025",
    description: "LLM-based SQL grading with 87% accuracy, built on a fine-tuned Llama 3.1",
    image: "/images/autograder.jpg",
    tech: ["python", "pytorch", "flask", "mongodb", "huggingface"],
  },
  {
    title: "Image Super Resolution",
    link: "https://github.com/ananya1305/Image-Super-Resolution",
    linkLabel: "GitHub",
    category: "Generative Models",
    description: "WGAN-GP with VGG19 perceptual loss for upscaling (PSNR 21.1, SSIM 0.74)",
    image: "/images/resolution.png",
    tech: ["python", "pytorch", "numpy", "opencv", "jupyter"],
  },
  {
    title: "AI-Driven Sales Forecasting",
    link: "https://github.com/ananya1305/AI-DRIVEN-SALES-FORECASTING",
    linkLabel: "GitHub",
    category: "Time Series",
    description: "ARIMA and seasonal decomposition pipeline with stationarity testing",
    image: "/images/forecast.png",
    tech: ["python", "pandas", "numpy", "matplotlib", "jupyter"],
  },
];

export const EXPERIENCE = [
  {
    role: "Graduate AI Engineer",
    company: "D'Amore-McKim AI Strategic Hub",
    dates: "Apr 2026 - Present",
    logo: "/images/logos/dmsb_logo.jpeg",
    illustration: "network",
    description:
      "Built a RAG-powered Canvas AI teaching assistant, an automated grading pipeline, and an LLM-as-judge regression testing platform.",
  },
  {
    role: "Teaching Assistant, RL",
    company: "Northeastern University",
    dates: "May 2026 - Aug 2026",
    logo: "/images/logos/neu_logo.png",
    illustration: "teach",
    description:
      "Graded assignments and held office hours for a graduate Reinforcement Learning course, breaking down core RL concepts and algorithms.",
  },
  {
    role: "Software Development Intern",
    company: "DigiPlus IT",
    dates: "Jan 2025 - Aug 2025",
    logo: "/images/logos/digiplus_logo.jpeg",
    illustration: "chat",
    description:
      "Shipped a production travel chatbot with LangGraph/RAG pipelines and n8n email automation, improving conversation quality by 40% for 500+ daily users.",
  },
  {
    role: "Software Development Intern",
    company: "Levana Technologies",
    dates: "May 2024 - Aug 2024",
    logo: null,
    illustration: "stack",
    description:
      "Built a full-stack company management platform with React, Node.js, Express and MongoDB.",
  },
];

// "What I excel at" carousel. Consecutive short cards stack into one column.
export const SERVICES = {
  intro:
    "I build AI products end to end: the model, the pipeline around it, and the app people actually use.",
  items: [
    {
      title: "AI Chatbots & Agents",
      text: "Production assistants on LangGraph and RAG that answer from your data, remember context and trigger real workflows.",
      visual: "chat",
      size: "tall",
      theme: "rose",
    },
    {
      title: "Backend Development",
      text: "Clean, fast APIs in FastAPI, Flask and Node, wired to vector stores and databases.",
      visual: "terminal",
      size: "short",
      theme: "ink",
    },
    {
      title: "LLM Evaluation",
      text: "LLM-as-judge regression suites that catch quality drops before users do.",
      visual: "eval",
      size: "short",
      theme: "coral",
    },
    {
      title: "Full-stack Apps",
      text: "React front ends on ML back ends, so a model becomes something people can actually use.",
      visual: "website",
      size: "tall",
      theme: "berry",
    },
    {
      title: "LLM Fine-tuning",
      text: "Adapting open models like Llama 3.1 with LoRA / PEFT, from dataset to evaluation.",
      visual: "code",
      size: "short",
      theme: "plum",
    },
    {
      title: "Applied ML & Vision",
      text: "Deep learning for images, from X-ray analysis to super-resolution.",
      visual: "vision",
      size: "short",
      theme: "rose",
      image: "/images/resolution.png",
    },
  ],
};

// Skills grid. icon: "devicon:<slug>" | "simple:<slug>" (brand logos from CDNs) | "glyph:<name>" (drawn in Skills.jsx).
export const SKILLS = {
  intro: "The languages, frameworks and tools I reach for when turning an AI idea into a working product.",
  categories: [
    {
      name: "AI & ML",
      items: [
        { name: "PyTorch", icon: "devicon:pytorch" },
        { name: "TensorFlow", icon: "devicon:tensorflow" },
        { name: "LangChain", icon: "simple:langchain" },
        { name: "LangGraph", icon: "simple:langgraph" },
        { name: "Hugging Face", icon: "simple:huggingface" },
        { name: "RAG", icon: "glyph:rag" },
        { name: "LLM Fine-tuning", icon: "glyph:tune" },
        { name: "LoRA / PEFT", icon: "glyph:lora" },
        { name: "NLP", icon: "glyph:nlp" },
        { name: "Deep Learning", icon: "glyph:brain" },
        { name: "Reinforcement Learning", icon: "glyph:rl" },
        { name: "Transformers", icon: "glyph:transformer" },
        { name: "Attention Mechanisms", icon: "glyph:attention" },
        { name: "Embeddings", icon: "glyph:embeddings" },
        { name: "CNNs", icon: "glyph:cnn" },
        { name: "GANs", icon: "glyph:gan" },
        { name: "U-Net++", icon: "glyph:unet" },
        { name: "EfficientNet", icon: "glyph:efficientnet" },
        { name: "OpenCV", icon: "devicon:opencv" },
        { name: "NumPy", icon: "devicon:numpy" },
        { name: "pandas", icon: "devicon:pandas" },
      ],
    },
    {
      name: "Languages",
      items: [
        { name: "Python", icon: "devicon:python" },
        { name: "JavaScript", icon: "devicon:javascript" },
        { name: "TypeScript", icon: "devicon:typescript" },
        { name: "C++", icon: "devicon:cplusplus" },
        { name: "SQL", icon: "glyph:database" },
        { name: "HTML", icon: "devicon:html5" },
        { name: "CSS", icon: "devicon:css3" },
      ],
    },
    {
      name: "Frontend",
      items: [
        { name: "React", icon: "devicon:react" },
        { name: "Next.js", icon: "devicon:nextjs" },
        { name: "Component Architecture", icon: "glyph:layers" },
      ],
    },
    {
      name: "Backend",
      items: [
        { name: "Node.js", icon: "devicon:nodejs" },
        { name: "FastAPI", icon: "devicon:fastapi" },
        { name: "Flask", icon: "devicon:flask" },
        { name: "REST APIs", icon: "glyph:api" },
      ],
    },
    {
      name: "Data & Tools",
      items: [
        { name: "MongoDB", icon: "devicon:mongodb" },
        { name: "Qdrant", icon: "simple:qdrant" },
        { name: "Docker", icon: "devicon:docker" },
        { name: "Git", icon: "devicon:git" },
        { name: "n8n", icon: "simple:n8n" },
        { name: "Power Apps", icon: "glyph:apps" },
      ],
    },
  ],
};

// Photos live in public/images/personal/. Any file that's missing is simply skipped.
export const PERSONAL = {
  nickname: "Ananya",
  words: ["curious", "adventurous", "ocean-obsessed", "caffeinated", "calm underwater"],
  hobbiesTitle: "Life outside the terminal",
  hobbiesIntro: "Tech keeps me curious, the ocean keeps me calm, and coffee keeps me functional.",
  hobbies: [
    {
      title: "The ocean",
      text: "My comfort place. I'm a national-level swimmer, and whether it's laps or open water, being in the water is where my head finally goes quiet.",
      emoji: "🌊",
      photo: "/images/personal/ocean.jpg",
      span: "lg:col-span-4",
    },
    {
      title: "Coffee",
      text: "Fuel for 2 AM debugging sessions and a lot of good conversations.",
      emoji: "☕",
      span: "lg:col-span-2",
    },
  ],
  adventure: {
    title: "Travel & adventure",
    text: "New places, new people, and anything that gets my heart rate up. I've jumped out of a plane for fun and I'm always planning the next trip.",
    // Icons hang from the rail; see components/ui/HangingIcons.jsx for the full set.
    icons: ["plane", "parachute", "waves", "camera", "compass", "laptop"],
  },
  gallery: [
    { src: "/images/personal/skydiving.jpg", caption: "Skydiving" },
    { src: "/images/personal/travel-1.jpg", caption: "On the road" },
    { src: "/images/personal/travel-2.jpg", caption: "Somewhere new" },
    { src: "/images/personal/ocean-2.jpg", caption: "Salt water therapy" },
  ],
  thanks: {
    line1: "Thank you",
    line2: "for visiting my profile",
  },
};
